import { Lead, Note } from '@/types';
import { displayLeadValue, leadDataPresentation, customFieldsRevision } from '@/lib/lead-data';
import { ChevronDown, Save, Tag, Hash, Globe, FileText, Mail, RefreshCw, ShoppingBag, Trash2, User, Store, Calendar, X, MessageCircle, Phone, Clock, Layers, Edit2, Check, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { api } from '@/services/api';
import { LeadAuditLogPanel } from './LeadAuditLogPanel';
import { useState, useEffect, useRef } from 'react';
import { formatSafe, cn, buildLeadUpdatePayload } from '@/lib/utils';
import { useConfigStore } from '@/store/configStore';
import { LeadAvatar } from '../LeadAvatar';
import { useLeadsStore } from '@/store/leadsStore';
import { useConversationsStore } from '@/store/conversationsStore';
import { useUsersStore } from '@/store/usersStore';
import { useLojasStore } from '@/store/lojasStore';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';
import { LeadMiniCalendar } from './LeadMiniCalendar';
import { LeadFullCalendar } from '../lead/LeadFullCalendar';
import { AppointmentModal } from '../calendar/AppointmentModal';
import { toast } from 'react-hot-toast';
import { TagSelector } from '@/components/common/TagSelector'; // [REFACTOR-TAGS]

interface Props {
  lead?: Lead;
  leadId?: string;
  onUpdate?: (updatedLead?: Partial<Lead>) => void;
  onDelete?: () => void;
  onClose?: () => void;
}

export function LeadProfileSidebar({ lead: propLead, leadId, onUpdate: propOnUpdate, onDelete, onClose }: Props) {
  const storeLead = useLeadsStore(state => state.leads.find(l => l.id === (leadId || propLead?.id)));
  const updateLeadLocally = useLeadsStore(state => state.updateLeadLocally);
  const lead = propLead || storeLead;

  const onUpdate = (updatedLead?: Partial<Lead>) => {
    if (propOnUpdate) {
      propOnUpdate(updatedLead);
    } else if (lead && updatedLead) {
      updateLeadLocally(lead.id, updatedLead);
    }
  };

  const navigate = useNavigate();
  const auditIdentity = useAuthStore(state => state.user?.id);
  const [savingNote, setSavingNote] = useState(false);
  const [note, setNote] = useState('');
  const [noteCategoria, setNoteCategoria] = useState('geral');
  const [notes, setNotes] = useState<Note[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingLead, setSavingLead] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);
  const [localLeadData, setLocalLeadData] = useState<Partial<Lead>>(lead ? { ...lead } : {});
  
  // Custom fields inline editing
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [savingField, setSavingField] = useState<string | null>(null);

  // Accordion / Collapsible section states
  const [showMetaAds, setShowMetaAds] = useState(false);
  const [showFormAnswers, setShowFormAnswers] = useState(false);
  const [showCrmData, setShowCrmData] = useState(false);
  const [showAppointments, setShowAppointments] = useState(false);

  const [appointmentModalOpen, setAppointmentModalOpen] = useState(false);
  const [fullCalendarOpen, setFullCalendarOpen] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);

  const { kanbanColumns, customFields: configuredFields, customFieldsStatus } = useConfigStore();
  const customFields = customFieldsStatus === 'loaded' && lead?.loja_id === useLojaAtivaStore.getState().lojaAtivaId ? configuredFields : [];
  const users = useUsersStore(state => state.users);
  const lojas = useLojasStore(state => state.lojas);
  const { lojaAtivaId } = useLojaAtivaStore();
  const refreshLeadAvatar = useLeadsStore(state => state.refreshLeadAvatar);
  const removeLeadLocally = useLeadsStore(state => state.removeLeadLocally);
  const [refreshingAvatar, setRefreshingAvatar] = useState(false);

  useEffect(() => {
    useUsersStore.getState().fetchUsers(true);
  }, [lojaAtivaId]);

  useEffect(() => {
    if (lead) {
      setLocalLeadData({ ...lead });
    }
  }, [lead]);

  const fetchNotes = async () => {
    if (!lead?.id) return;
    setLoadingNotes(true);
    try {
      const data = await api.getNotes(lead.id);
      setNotes(data || []);
    } catch (e) {
      console.error('[LEAD_SIDEBAR] Erro ao buscar notas:', e);
    } finally {
      setLoadingNotes(false);
    }
  };

  useEffect(() => {
    if (lead?.id) {
      fetchNotes();
    }
  }, [lead?.id]);



  const fieldSaveLock = useRef(false);
  const editBase = useRef({ id: '', revision: '' });
  const activeLeadId = useRef(lead?.id);
  activeLeadId.current = lead?.id;

  if (!lead) return null;

  const handleSaveNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await api.addNote(lead.id, note, noteCategoria);
      setNote('');
      await fetchNotes();
      onUpdate();
      toast.success('Nota salva com sucesso!');
    } catch (e) {
      toast.error('Erro ao salvar nota.');
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = async (noteId: string | number) => {
    try {
      await api.deleteNote(String(noteId));
      toast.success('Nota excluída!');
      await fetchNotes();
    } catch (e) {
      toast.error('Erro ao excluir nota.');
    }
  };

  const setField = (field: keyof Lead, value: any) => {
    setLocalLeadData(prev => ({ ...prev, [field]: value }));
  };

  const setQualificacao = (key: string, value: any) => {
    setLocalLeadData(prev => ({
      ...prev,
      dados_qualificacao: {
        ...(prev.dados_qualificacao || {}),
        [key]: value
      }
    }));
  };

  const handleStartEditField = (fieldKey: string, currentValue: any) => {
    editBase.current = { id: lead.id, revision: customFieldsRevision(localLeadData.metadata) };
    setEditingField(fieldKey);
    setEditValue(currentValue === true ? 'sim' : currentValue === false ? 'nao' : currentValue !== undefined && currentValue !== null ? String(currentValue) : '');
  };

  const handleSaveField = async (fieldKey: string) => {
    if (fieldSaveLock.current || editBase.current.id !== lead.id) return;
    const row = leadDataPresentation(localLeadData.metadata, localLeadData.campos_personalizados, customFields, localLeadData).rows.find(r => r.key === fieldKey);
    if (!row?.editable) return;
    const originalText = row.value == null ? '' : typeof row.value === 'boolean' ? (row.value ? 'sim' : 'nao') : String(row.value);
    if (editValue === originalText) { setEditingField(null); return; }
    const fieldDef = customFields.find(f => f.key === fieldKey)!;
    let value: any = editValue;
    if (editValue === '') value = null;
    else if (fieldDef.type === 'number') {
      if (!/^[+-]?\d+(?:[.,]\d+)?$/.test(editValue.trim())) { toast.error('Número inválido.'); return; }
      value = Number(editValue.replace(',', '.'));
    } else if (fieldDef.type === 'boolean') value = editValue === 'sim';
    fieldSaveLock.current = true;
    setSavingField(fieldKey);
    try {
      // Send only the intentional edit. Never replay sibling values or acquisition metadata.
      const updatedLead = await api.updateLead(lead.id, {
        campos_personalizados: { [fieldKey]: value },
        expected_custom_fields_revision: editBase.current.revision
      });
      if (activeLeadId.current !== updatedLead.id) return;
      setLocalLeadData(updatedLead);
      onUpdate(updatedLead);
      useLeadsStore.getState().updateLeadLocally(lead.id, updatedLead);
      setEditingField(null);
      toast.success('Campo atualizado!');
    } catch (err: any) {
      // Pessimistic save: the confirmed lead remains unchanged and the draft is retained.
      toast.error(err.message || 'Erro ao salvar campo. Reabra o lead para recarregar.');
    } finally { fieldSaveLock.current = false; setSavingField(null); }
  };

  const handleSaveLead = async () => {
    setSavingLead(true);
    
    const previousLead = { ...lead };
    onUpdate({ ...localLeadData });
    useLeadsStore.getState().updateLeadLocally(lead.id, localLeadData);
    
    try {
      const payload = buildLeadUpdatePayload(localLeadData);
      const updatedLead = await api.updateLead(lead.id, payload);
      onUpdate(updatedLead);
      useLeadsStore.getState().updateLeadLocally(lead.id, updatedLead);
      toast.success('Alterações salvas!');
    } catch (e) {
      onUpdate(previousLead);
      useLeadsStore.getState().updateLeadLocally(lead.id, previousLead);
      toast.error('Erro ao atualizar dados.');
    } finally {
      setSavingLead(false);
    }
  };

  const handleDeleteLead = async () => {
    if (deletingLead) return;
    if (!window.confirm('Tem certeza que deseja deletar este lead permanentemente?')) {
      return;
    }
    setDeletingLead(true);
    try {
      await api.deleteLead(lead.id); // 404 (já apagado) conta como sucesso
      // Some de todas as listas: leads (Kanban/lista) e conversas (Inbox), senão o chat continua montado.
      removeLeadLocally(lead.id);
      useConversationsStore.getState().removeLeadConversations(lead.id);
      toast.success('Lead deletado com sucesso!');
      if (onDelete) onDelete();
      if (onClose) onClose();
    } catch (e: any) {
      toast.error(e.message || 'Erro ao deletar lead.');
      setDeletingLead(false);
    }
  };

  const metaAds = lead.metadata?.meta_ads;
  const presentation = leadDataPresentation(localLeadData.metadata, localLeadData.campos_personalizados, customFields, localLeadData);
  const customFormFields = Object.fromEntries(presentation.unmapped);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-xs p-0 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div 
        className="bg-white dark:bg-[#121417] rounded-t-3xl sm:rounded-3xl shadow-2xl w-full max-w-xl max-h-[92dvh] sm:max-h-[92vh] flex flex-col overflow-hidden border border-gray-100 dark:border-gray-800 my-0 sm:my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header do Modal com estilo limpo minimalista */}
        <div className="flex items-start justify-between p-4 sm:p-5 pb-3 border-b border-gray-100 dark:border-gray-800/80 sticky top-0 bg-white/95 dark:bg-[#121417]/95 backdrop-blur-md z-10">
          <div className="flex-1 pr-3">
            <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">
              DETALHES DO LEAD
            </span>
            <input 
              type="text"
              value={localLeadData.nome || ''}
              onChange={e => setField('nome', e.target.value)}
              placeholder="Nome do Lead"
              className="w-full text-xl font-bold text-gray-900 dark:text-gray-100 bg-transparent border-b border-transparent hover:border-gray-200 dark:hover:border-gray-700 focus:border-blue-500 focus:outline-none transition-colors"
            />
          </div>
          {onClose && (
            <button 
              onClick={onClose} 
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/60 rounded-full transition-colors shrink-0"
              title="Fechar"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Conteúdo Principal Scrollável */}
        <div className="p-4 sm:p-5 overflow-y-auto space-y-3 sm:space-y-4 text-xs">

          {/* Card 1: Etapa Atual no Kanban */}
          <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl p-4 shadow-2xs flex items-center justify-between gap-3">
            <span className="font-semibold text-gray-800 dark:text-gray-200 text-xs flex items-center gap-2">
              <Layers className="w-4 h-4 text-gray-400 shrink-0" />
              Etapa Atual no Kanban:
            </span>
            <div className="relative min-w-[170px]">
              <select 
                value={localLeadData.etapa_id || ''}
                onChange={async (e) => {
                  const newEtapaId = e.target.value || null;
                  const selectedCol = kanbanColumns.find(c => c.id === e.target.value);
                  const newStatusAtendimento = selectedCol?.title || null;
                  
                  setField('etapa_id', newEtapaId);
                  if (selectedCol) {
                    setField('status_atendimento', newStatusAtendimento);
                  }

                  const optimisticUpdate = {
                    etapa_id: newEtapaId,
                    status_atendimento: newStatusAtendimento
                  };

                  onUpdate(optimisticUpdate);
                  useLeadsStore.getState().updateLeadLocally(lead.id, optimisticUpdate);

                  try {
                    const updated = await api.updateLead(lead.id, { 
                      etapa_id: newEtapaId,
                      status_atendimento: newStatusAtendimento
                    });
                    if (updated) {
                      onUpdate(updated);
                      useLeadsStore.getState().updateLeadLocally(lead.id, updated);
                    }
                  } catch (err: any) {
                    console.error('[LEAD_UPDATE_ETAPA_ERR]', err);
                    toast.error('Erro ao atualizar etapa do lead.');
                  }
                }}
                className="w-full pl-3 pr-8 py-1.5 bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-bold text-gray-800 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 cursor-pointer text-right"
              >
                <option value="">— Selecionar —</option>
                {kanbanColumns.map(col => (
                  <option key={col.id} value={col.id}>{col.title}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Card 2: Contatos (Telefone/WhatsApp e E-mail em Grid 2 colunas) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Box Telefone */}
            <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl p-3.5 shadow-2xs space-y-1">
              <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block">
                TELEFONE / WHATSAPP
              </label>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                <input 
                  type="text"
                  value={localLeadData.telefone || ''}
                  onChange={e => setField('telefone', e.target.value)}
                  placeholder="+55..."
                  className="w-full font-bold text-gray-900 dark:text-gray-100 bg-transparent border-none focus:outline-none text-xs"
                />
              </div>
            </div>

            {/* Box E-mail */}
            <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl p-3.5 shadow-2xs space-y-1">
              <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider block">
                E-MAIL
              </label>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <input 
                  type="email"
                  value={localLeadData.email || ''}
                  onChange={e => setField('email', e.target.value)}
                  placeholder="email@exemplo.com"
                  className="w-full font-bold text-gray-900 dark:text-gray-100 bg-transparent border-none focus:outline-none text-xs"
                />
              </div>
            </div>
          </div>

          {/* Card: DADOS PERSONALIZADOS (com edição inline) */}
          {(() => {
            if (presentation.rows.length === 0) return null;

            return (
              <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl p-4 shadow-2xs space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-500" />
                    Dados do lead
                  </label>
                  <span className="text-[10px] text-gray-400">Clique para editar</span>
                </div>

                <div className="space-y-2 divide-y divide-gray-100 dark:divide-gray-800/60">
                  {presentation.rows.map(row => {
                    const { key, label, value } = row;
                    const fieldDef = customFields.find(f => f.key === key);
                    const isEditing = editingField === key;
                    const displayValue = displayLeadValue(value);

                    return (
                      <div key={key} className="flex items-center justify-between pt-2 first:pt-0 text-xs">
                        <div className="min-w-0">
                          <span className="text-gray-500 dark:text-gray-400 font-medium">{label}:</span>
                          {row.source && <span className="block text-[10px] text-gray-400">{row.source}</span>}
                          {row.originals.length > 0 && <details className="text-[10px] text-gray-500"><summary className="cursor-pointer">Ver resposta original</summary>{row.originals.map(([question, original]) => <p key={question}>{question}: {displayLeadValue(original)}</p>)}</details>}
                        </div>

                        {isEditing ? (
                          <div className="flex items-center gap-1.5">
                            {fieldDef?.type === 'boolean' ? (
                              <select
                                autoFocus
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleSaveField(key)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveField(key);
                                  if (e.key === 'Escape') setEditingField(null);
                                }}
                                className="px-2 py-1 text-xs border rounded-lg border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white cursor-pointer"
                              >
                                <option value="">Limpar</option>
                                <option value="sim">Sim</option>
                                <option value="nao">Não</option>
                              </select>
                            ) : fieldDef?.type === 'date' ? (
                              <input
                                autoFocus
                                type="date"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleSaveField(key)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveField(key);
                                  if (e.key === 'Escape') setEditingField(null);
                                }}
                                className="px-2 py-1 text-xs border rounded-lg border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                              />
                            ) : (
                              <input
                                autoFocus
                                type={fieldDef?.type === 'number' ? 'number' : 'text'}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onBlur={() => handleSaveField(key)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveField(key);
                                  if (e.key === 'Escape') setEditingField(null);
                                }}
                                className="px-2 py-1 text-xs border rounded-lg border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white max-w-[140px]"
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => handleSaveField(key)}
                              className="p-1 text-emerald-600 hover:text-emerald-700 rounded hover:bg-emerald-50 cursor-pointer"
                              title="Salvar"
                            >
                              <Check size={14} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={!row.editable || !!savingField}
                            onClick={() => handleStartEditField(key, value)}
                            className="group flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 font-bold text-gray-900 dark:text-gray-100 text-right transition-colors cursor-pointer"
                            title={row.editable ? "Clique para editar" : "Somente leitura: definição ou tipo não editável"}
                          >
                            <span>{displayValue}</span>
                            <Edit2 size={11} className="text-gray-300 group-hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Card 3: METADADOS META ADS (Com opção de abrir/esconder) */}
          {metaAds && (
            <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl shadow-2xs overflow-hidden">
              <button
                type="button"
                onClick={() => setShowMetaAds(!showMetaAds)}
                className="w-full flex items-center justify-between p-4 text-left font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors"
              >
                <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">
                  <Tag className="w-4 h-4 text-blue-500" />
                  Origem e atribuição
                </span>
                <ChevronDown className={cn("w-4 h-4 transition-transform text-gray-400", showMetaAds && "rotate-180")} />
              </button>

              {showMetaAds && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100 dark:border-gray-800/60">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                    {metaAds.campaign?.name && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 font-medium">Campanha: </span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">{metaAds.campaign.name}</span>
                      </div>
                    )}
                    {metaAds.ad?.name && (
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 font-medium">Anúncio: </span>
                        <span className="font-bold text-gray-800 dark:text-gray-200">{metaAds.ad.name}</span>
                      </div>
                    )}
                  </div>
                  {metaAds.created_time && (
                    <div className="flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500 pt-1 border-t border-gray-100 dark:border-gray-800/40 font-medium">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Recebido em: {new Date(metaAds.created_time).toLocaleString('pt-BR')}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Card 4: RESPOSTAS DO FORMULÁRIO CUSTOMIZADO (META ADS) */}
          {customFormFields && Object.keys(customFormFields).length > 0 && (
            <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl shadow-2xs overflow-hidden">
              <button
                type="button"
                onClick={() => setShowFormAnswers(!showFormAnswers)}
                className="w-full flex items-center justify-between p-4 text-left font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors"
              >
                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Respostas sem vínculo (somente consulta)
                </span>
                <ChevronDown className={cn("w-4 h-4 transition-transform text-gray-400", showFormAnswers && "rotate-180")} />
              </button>

              {showFormAnswers && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-gray-100 dark:border-gray-800/60">
                  {Object.entries(customFormFields).map(([key, value]: [string, any]) => (
                    <div key={key} className="space-y-0.5">
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium">
                        {key.replace(/_/g, ' ').replace(/\?/g, '')}
                      </p>
                      <p className="text-xs font-bold text-gray-900 dark:text-gray-100">
                        {displayLeadValue(value)}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Card 5: Observações Internas */}
          <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl p-4 shadow-2xs space-y-2">
            <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400" />
              Observações Internas
            </label>
            <textarea 
              value={localLeadData.observacoes || ''}
              onChange={e => setField('observacoes', e.target.value)}
              placeholder="Digite observações sobre o lead..."
              className="w-full bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700/80 rounded-xl p-3 text-xs font-medium text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:border-blue-500 resize-none transition-colors"
              rows={3}
            />
          </div>

          {/* Card: Anotações com Categoria */}
          <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl p-4 shadow-2xs space-y-2">
            <label className="text-[11px] font-bold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <FileText className="w-4 h-4 text-blue-500" />
              Anotações do Lead
            </label>
            <textarea 
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Digite uma nova anotação..."
              className="w-full bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700/80 rounded-xl p-3 text-xs font-medium text-gray-800 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:border-blue-500 resize-none transition-colors"
              rows={2}
            />
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-gray-500 uppercase">Categoria:</span>
                <select
                  value={noteCategoria}
                  onChange={e => setNoteCategoria(e.target.value)}
                  className="text-xs bg-gray-50 dark:bg-[#1A1D21] border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 text-gray-800 dark:text-gray-200"
                >
                  <option value="geral">Geral</option>
                  <option value="objecao">Objeção</option>
                  <option value="desqualificado">Desqualificado</option>
                  <option value="nada">Nada</option>
                </select>
              </div>
              <button
                type="button"
                onClick={handleSaveNote}
                disabled={savingNote || !note.trim()}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all cursor-pointer"
              >
                {savingNote ? 'Salvando...' : 'SALVAR NOTA'}
              </button>
            </div>

            {/* Lista de anotações existentes */}
            {notes.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800/60 space-y-2">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Histórico de Anotações ({notes.length})
                </span>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                  {notes.map((n) => {
                    const catColors: Record<string, string> = {
                      objecao: 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300 border-amber-200 dark:border-amber-800',
                      desqualificado: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300 border-red-200 dark:border-red-800',
                      nada: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700',
                      geral: 'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300 border-blue-200 dark:border-blue-800',
                    };
                    const badgeClass = catColors[n.categoria || 'geral'] || catColors.geral;

                    return (
                      <div
                        key={n.id}
                        className="p-2.5 bg-gray-50 dark:bg-[#22262B] border border-gray-100 dark:border-gray-700/60 rounded-xl text-xs space-y-1 group"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border", badgeClass)}>
                            {n.categoria || 'Geral'}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-[10px] text-gray-400">
                              {n.created_at ? formatSafe(n.created_at, "dd/MM 'às' HH:mm") : ''}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleDeleteNote(n.id)}
                              className="text-gray-400 hover:text-red-500 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              title="Excluir anotação"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                        <p className="text-gray-800 dark:text-gray-200 text-xs whitespace-pre-wrap leading-relaxed">
                          {n.note}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Secao Colapsavel: Outros Dados & Configurações CRM */}
          <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl shadow-2xs">
            <button
              type="button"
              onClick={() => setShowCrmData(!showCrmData)}
              className="w-full flex items-center justify-between p-4 text-left font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50/50 dark:hover:bg-gray-800/40 rounded-2xl transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-2.5 text-xs font-bold text-gray-700 dark:text-gray-200">
                <Store className="w-4 h-4 text-blue-500" />
                Mais Dados do CRM (Loja, Responsável, Origem, Tags)
              </span>
              <ChevronDown className={cn("w-4 h-4 transition-transform text-gray-400", showCrmData && "rotate-180")} />
            </button>

            {showCrmData && (
              <div className="p-4 pt-2 space-y-4 border-t border-gray-100 dark:border-gray-800/60">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider block">
                      Loja:
                    </label>
                    <select
                      value={localLeadData.loja_id || ''}
                      onChange={e => setField('loja_id', e.target.value || null)}
                      className="w-full text-xs font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
                    >
                      <option value="">— Selecionar loja —</option>
                      {lojas.map(l => (
                        <option key={l.id} value={l.id}>{l.nome}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider block">
                      Responsável:
                    </label>
                    <select 
                      value={localLeadData.responsavel_user_id || ''}
                      onChange={e => setField('responsavel_user_id', e.target.value)}
                      className="w-full text-xs font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
                    >
                      <option value="">Não atribuído</option>
                      {users.map(u => (
                        <option key={u.id} value={u.id}>{u.nome}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider block">
                      Origem:
                    </label>
                    <input 
                      type="text"
                      value={localLeadData.origem || ''}
                      onChange={e => setField('origem', e.target.value)}
                      placeholder="Ex: WhatsApp, Instagram, Google..."
                      className="w-full text-xs font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider block">
                      Produto de Interesse:
                    </label>
                    <input 
                      type="text"
                      value={localLeadData.produto_interesse || ''}
                      onChange={e => setField('produto_interesse', e.target.value)}
                      placeholder="Ex: Produto / Serviço..."
                      className="w-full text-xs font-medium text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-[#22262B] border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                    />
                  </div>
                </div>

                <div className="space-y-1.5 pt-1">
                  <label className="text-[11px] font-bold text-gray-600 dark:text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5 text-blue-500" />
                    Tags do Lead:
                  </label>
                  <TagSelector
                    lojaId={localLeadData.loja_id || lead.loja_id || lojaAtivaId}
                    selectedTags={localLeadData.tags || []}
                    onChange={(tags) => setField('tags', tags)}
                    placeholder="Buscar ou adicionar tags..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Secao Colapsavel: Agendamentos */}
          <div className="bg-white dark:bg-[#1A1D21] border border-gray-200/80 dark:border-gray-800 rounded-2xl shadow-2xs overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAppointments(!showAppointments)}
              className="w-full flex items-center justify-between p-4 text-left font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50/50 dark:hover:bg-gray-800/40 transition-colors"
            >
              <span className="flex items-center gap-2 text-xs font-bold text-gray-600 dark:text-gray-300">
                <Calendar className="w-4 h-4 text-gray-400" />
                Agendamentos & Visitas
              </span>
              <ChevronDown className={cn("w-4 h-4 transition-transform text-gray-400", showAppointments && "rotate-180")} />
            </button>

            {showAppointments && (
              <div className="p-4 pt-1 space-y-3 border-t border-gray-100 dark:border-gray-800/60">
                <button
                  onClick={() => setAppointmentModalOpen(true)}
                  className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2"
                >
                  <Calendar size={14} /> Criar Agendamento
                </button>

                <LeadMiniCalendar key={calendarRefreshKey} leadId={lead.id} />

                <button
                  onClick={() => setFullCalendarOpen(true)}
                  className="w-full py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 border border-gray-200 dark:border-gray-700"
                >
                  Ver Agenda Completa
                </button>
              </div>
            )}
          </div>

          <LeadAuditLogPanel leadId={lead.id} scope={JSON.stringify([auditIdentity, lead.tenant_id, lead.loja_id, lojaAtivaId])} />

        </div>

        {/* Rodapé do Modal (Fixo com botões no padrão da imagem) */}
        <div className="p-4 border-t border-gray-100 dark:border-gray-800/80 sticky bottom-0 bg-white/95 dark:bg-[#121417]/95 backdrop-blur-md flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                navigate(`/inbox?lead_id=${lead.id}`);
                if (onClose) onClose();
              }}
              className="p-2.5 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400 rounded-xl font-bold text-xs transition-colors flex items-center gap-1.5 border border-emerald-200/50 dark:border-emerald-800/40"
              title="Abrir Conversa"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Conversa</span>
            </button>

            <button
              onClick={handleDeleteLead}
              disabled={deletingLead}
              className="p-2.5 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-xl font-bold text-xs transition-colors disabled:opacity-50"
              title="Deletar Lead"
              aria-busy={deletingLead}
            >
              {deletingLead ? <span className="text-xs">Apagando…</span> : <Trash2 className="w-4 h-4" />}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-xs transition-colors cursor-pointer"
              >
                Fechar
              </button>
            )}

            <button
              type="button"
              onClick={handleSaveLead}
              disabled={savingLead}
              className="px-5 py-2.5 bg-[#0F172A] hover:bg-[#1E293B] dark:bg-blue-600 dark:hover:bg-blue-500 text-white rounded-xl font-bold text-xs transition-all shadow-sm flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
            >
              <Save size={14} />
              {savingLead ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      </div>

      {appointmentModalOpen && (
        <AppointmentModal
          open={appointmentModalOpen}
          isOpen={appointmentModalOpen}
          initialLeadId={lead.id}
          initialLead={lead}
          leadId={lead.id}
          leadName={lead.nome || lead.telefone || 'Lead'}
          lojaId={lead.loja_id}
          onClose={() => setAppointmentModalOpen(false)}
          onSaved={() => {
            setAppointmentModalOpen(false);
            toast.success('Agendamento criado!');
            setCalendarRefreshKey(prev => prev + 1);
          }}
        />
      )}

      {fullCalendarOpen && (
        <LeadFullCalendar
          open={fullCalendarOpen}
          onClose={() => setFullCalendarOpen(false)}
          leadId={lead.id}
          leadName={lead.nome || lead.telefone || 'Lead'}
          lojaId={lead.loja_id || undefined}
        />
      )}
    </div>
  );
}
